import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Form } from 'antd';
import FormSelect from '../../../../../antd/form/FormSelect/FormSelect.jsx';
import { required } from '../../../../../antd/form/FormValidation.js';
import {
  useGetArticleAuthorsQuery,
  useGetNewsUsersQuery,
} from '../../../../../../redux/services/users.jsx';
import usePermission from '../../../../../../hooks/usePermission.jsx';
import { formattedFullName } from '../../../../../../utils/helpers/CommonHelper.jsx';

const WritersPicker = ({
  writerParams = 'news',
  permission,
  currentWriterName,
  isCreate,
  formInstance,
  extra,
}) => {
  const { t } = useTranslation();
  const hasPermission = usePermission(permission);
  const contextForm = Form.useFormInstance();
  const form = formInstance ?? contextForm;
  const normalizeId = (value) =>
    value === null || value === undefined || value === ''
      ? undefined
      : String(value);

  const effectiveSelectedPersonId = normalizeId(Form.useWatch('person_id', form));

  useEffect(() => {
    if (!form) {
      return;
    }

    const targetValue = normalizeId(form.getFieldValue('person_id'));

    if (targetValue && form.getFieldValue('person_id') !== targetValue) {
      form.setFieldValue('person_id', targetValue);
    }
  }, [form]);

  const { data: newsUsers } = useGetNewsUsersQuery(undefined, {
    skip: !hasPermission || writerParams !== 'news',
  });
  const { data: articleAuthors } = useGetArticleAuthorsQuery(undefined, {
    skip: !hasPermission || writerParams !== 'articles',
  });
  const newsWriters = writerParams === 'articles' ? articleAuthors : newsUsers;

  const writersOptions = useMemo(
    () => {
      const options = (newsWriters || [])
        .map(
          ({
            id,
            person_id,
            first_name,
            last_name,
            title_before,
            title_after,
          }) => ({
            value: normalizeId(person_id ?? id),
            label: formattedFullName({
              first_name,
              last_name,
              title_before,
              title_after,
            }),
            last_name: last_name ?? '',
            first_name: first_name ?? '',
          }),
        )
        .sort(
          (a, b) =>
            a.last_name.localeCompare(b.last_name) ||
            a.first_name.localeCompare(b.first_name),
        );

      if (
        effectiveSelectedPersonId &&
        !options.some(({ value }) => value === effectiveSelectedPersonId)
      ) {
        options.unshift({
          value: effectiveSelectedPersonId,
          label: currentWriterName || String(effectiveSelectedPersonId),
        });
      }

      return options;
    },
    [newsWriters, effectiveSelectedPersonId, currentWriterName],
  );

  if (!hasPermission) {
    return null;
  }

  return (
    <FormSelect
      name="person_id"
      label={t('Vytvorené používateľom')}
      options={writersOptions}
      rules={isCreate ? [] : [required(t)]}
      initialValue={effectiveSelectedPersonId}
      loading={!newsWriters}
      extra={extra}
    />
  );
};

WritersPicker.propTypes = {
  writerParams: PropTypes.oneOf(['news', 'articles']),
  permission: PropTypes.string,
  currentWriterName: PropTypes.string,
  isCreate: PropTypes.bool,
  formInstance: PropTypes.object,
  extra: PropTypes.node,
};

export default WritersPicker;
